import React, { useState } from 'react';
import { MdEmail, MdSend } from 'react-icons/md';
import { FaTwitter, FaGithub, FaLinkedin } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { glassStyle } from './themes';

const SOCIAL = [
  { icon: FaTwitter,  href: '#', label: 'Twitter' },
  { icon: FaGithub,   href: '#', label: 'GitHub' },
  { icon: FaLinkedin, href: '#', label: 'LinkedIn' },
];

/** Side-by-side info + contact form. Submits to the contact-us API. */
export default function ContactUs({ t }) {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);

  const inputStyle = {
    width: '100%', height: 48, background: t.inputBg, border: `1px solid ${t.inputBorder}`,
    borderRadius: 12, color: t.text, padding: '0 14px', fontSize: 14,
    outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s',
  };
  const focusBorder = (e) => { e.target.style.borderColor = 'rgba(139,92,246,0.6)'; };
  const blurBorder  = (e) => { e.target.style.borderColor = t.inputBorder; };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error('Please fill all fields'); return; }
    setLoading(true);
    try {
      await API.sendContracUs({ name: form.name, email_address: form.email, message: form.message });
      toast.success('Message sent! We\'ll get back to you soon.');
      setForm({ name: '', email: '', message: '' });
    } catch {
      toast.error('Failed to send. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="contact" style={{ maxWidth: 1200, margin: '0 auto', padding: '80px 24px', position: 'relative', zIndex: 1 }}>
      <div style={{ textAlign: 'center', marginBottom: 56 }}>
        <h2 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 800, color: t.text, letterSpacing: '-1px', margin: '0 0 14px' }}>Get in touch</h2>
        <p style={{ color: t.textSub, fontSize: 18, margin: 0 }}>Have questions? We'd love to hear from you.</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))', gap: 32, alignItems: 'start' }}>
        <div>
          <div style={{ ...glassStyle(t), padding: 32, marginBottom: 18 }}>
            <h3 style={{ color: t.text, fontWeight: 700, fontSize: 20, margin: '0 0 12px' }}>Contact Information</h3>
            <p style={{ color: t.textSub, fontSize: 15, lineHeight: 1.7, margin: '0 0 24px' }}>
              Our team is here to help you build, launch, and scale your brand.
              Reach out any time — we typically respond within a few hours.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <a href="mailto:hello@techproone.com" style={{ color: '#A78BFA', fontSize: 14, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
                <MdEmail size={16} /> hello@techproone.com
              </a>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            {SOCIAL.map(({ icon: Icon, href, label }) => (
              <a
                key={label} href={href} title={label}
                style={{ width: 44, height: 44, borderRadius: 12, ...glassStyle(t), display: 'flex', alignItems: 'center', justifyContent: 'center', color: t.textSub, textDecoration: 'none', transition: 'all 0.2s' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = t.surfaceHover; e.currentTarget.style.color = '#A78BFA'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = t.surface; e.currentTarget.style.color = t.textSub; }}
              >
                <Icon size={18} />
              </a>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ ...glassStyle(t), padding: 32, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ display: 'block', color: t.textSub, fontSize: 12, marginBottom: 6 }}>Your Name</label>
            <input style={inputStyle} placeholder="John Doe" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} onFocus={focusBorder} onBlur={blurBorder} />
          </div>
          <div>
            <label style={{ display: 'block', color: t.textSub, fontSize: 12, marginBottom: 6 }}>Email Address</label>
            <input style={inputStyle} type="email" placeholder="you@example.com" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} onFocus={focusBorder} onBlur={blurBorder} />
          </div>
          <div>
            <label style={{ display: 'block', color: t.textSub, fontSize: 12, marginBottom: 6 }}>Message</label>
            <textarea
              style={{ ...inputStyle, height: 120, padding: '12px 14px', resize: 'vertical', lineHeight: 1.6 }}
              placeholder="Tell us how we can help..."
              value={form.message}
              onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
              onFocus={focusBorder} onBlur={blurBorder}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            style={{ height: 48, borderRadius: 12, background: 'linear-gradient(135deg, #7C3AED, #4F46E5)', color: '#fff', fontWeight: 600, fontSize: 15, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: loading ? 0.7 : 1, transition: 'all 0.2s' }}
            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.opacity = '0.88'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = loading ? '0.7' : '1'; }}
          >
            <MdSend size={16} /> {loading ? 'Sending…' : 'Send Message'}
          </button>
        </form>
      </div>
    </div>
  );
}
